import {
    ChangeDetectionStrategy, Component, OnDestroy, OnInit,
    inject, signal, computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FieldExecutionService, BlockGroup, VillageGroup } from '../../../services/field-execution.service';
import { Project } from '../../../models/project.model';
import { AuthService } from '../../../services/auth.service';
import {
    aggregateGpStatusStats,
    formatCostLakh,
    isFeDelayed,
    isFeInProgress,
    isFeOnTrack,
    rollupProjectsForGpTotals,
} from '../../../utils/field-execution-stats.utils';
import { splitExecutantNames } from '../../../utils/executant-name.utils';

type Screen = 'villages' | 'projects';
type FeReturnScreen = Screen;

interface FeExecutantNavState {
    returnUrl?: string;
    returnGp?: string;
    returnScreen?: FeReturnScreen;
}

interface FeVillageListNavState {
    openGp?: string;
    screen?: FeReturnScreen;
}
type ProjectSortOption = 'criticality' | 'alphabetical' | 'cost';
type GpTableSortKey = 'village' | 'scheme' | 'name' | 'cost' | 'executant' | 'contact' | 'status';

type VlListFilter =
  // block (villages) screen
  | 'totalGPs' | 'totalProjectsBlock' | 'sanctionedBlock'
  | 'onTrackBlock' | 'inProgressBlock' | 'delayedBlock'
  | 'avgProgressBlock' | 'stuckBlock'
  // GP (projects) screen
  | 'totalProjectsGP' | 'totalCostGP'
  | 'onTrackGP' | 'inProgressGP' | 'delayedGP'
  | 'stuckGP' | 'rivalryGP';

@Component({
    selector: 'app-village-list',
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    templateUrl: './village-list.component.html',
    styleUrl: './village-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VillageListComponent implements OnInit, OnDestroy {
    private readonly router   = inject(Router);
    private readonly route    = inject(ActivatedRoute);
    private readonly svc      = inject(FieldExecutionService);
    private readonly fb       = inject(FormBuilder);
    private readonly auth     = inject(AuthService);
    private readonly destroy$ = new Subject<void>();
    private timerHandle: any;

    currentTime = signal(new Date());
    blockName = signal<string>('');
    screen    = signal<Screen>('villages');

    displayBlockName = computed(() => {
        if (!this.auth.isMinisterOrOsd()) return this.blockName();
        if (this.blockName() === 'Dhamanagar') return 'Dhamnagar Block';
        if (this.blockName() === 'Tihidi')     return 'Tihidi Block';
        return this.blockName();
    });

    isNacBlock    = computed(() => /\bnac\b/i.test(this.blockName()));
    gpLabel       = computed(() => this.isNacBlock() ? 'NAC' : 'GP');
    gpLabelFull   = computed(() => this.isNacBlock() ? 'NAC' : 'Gram Panchayat');
    gpLabelPlural = computed(() => this.isNacBlock() ? 'NACs' : 'GPs');

    villageLabel(name: string): string {
        return this.isNacBlock() ? name.toUpperCase() : name;
    }

    selectedBlock   = signal<BlockGroup | null>(null);
    selectedVillage = signal<VillageGroup | null>(null);

    isLoading    = signal(true);
    searchTerm   = signal('');
    statusFilter = signal<string>('');
    yearFilter   = signal<string>('');
    schemeFilter = signal<string>('');

    readonly statusOptions = ['', 'Planned', 'In Progress', 'Completed', 'Stuck'];

    blockStuckNotStarted = computed(() => {
        const block = this.selectedBlock();
        if (!block) return { count: 0, percent: 0 };
        return this.stuckNotStartedStats(block.villages.flatMap(v => v.projects));
    });

    filteredVillages = computed<VillageGroup[]>(() => {
        const block = this.selectedBlock();
        if (!block) return [];
        const q = this.searchTerm().toLowerCase().trim();
        return block.villages
            .filter(v =>
                !q ||
                v.village.toLowerCase().includes(q) ||
                v.projects.some(p => p.name.toLowerCase().includes(q))
            )
            .sort((a, b) =>
                (a.village || '').trim().localeCompare((b.village || '').trim(), undefined, { sensitivity: 'base' })
            );
    });

    availableYears = computed<string[]>(() => {
        const v = this.selectedVillage();
        if (!v) return [];
        const years = new Set(
            v.projects.map(p => p.sanctionedYear).filter((y): y is string => !!y)
        );
        return [...years].sort();
    });

    availableSchemes = computed<string[]>(() => {
        const v = this.selectedVillage();
        if (!v) return [];
        const schemes = new Set(
            v.projects.map(p => p.scheme).filter((s): s is string => !!s)
        );
        return [...schemes].sort();
    });

    filteredProjects = computed<Project[]>(() => {
        const village = this.selectedVillage();
        if (!village) return [];
        const q  = this.searchTerm().toLowerCase().trim();
        const sf = this.statusFilter();
        return village.projects
            .filter(p => {
                const matchQ  = !q  || p.name.toLowerCase().includes(q);
                const matchSf = !sf || p.status === sf;
                return matchQ && matchSf;
            })
            .sort((a, b) =>
                (a.name || '').trim().localeCompare((b.name || '').trim(), undefined, { sensitivity: 'base' }) ||
                (a.village || a.loc || '').trim().localeCompare((b.village || b.loc || '').trim(), undefined, { sensitivity: 'base' })
            );
    });

    // ── GP project view ────────────────────────────────────────────────────
    activeQuickFilter = signal<string>('');
    projectSortBy     = signal<ProjectSortOption>('criticality');

    gpSummary = computed(() => {
        const v = this.selectedVillage();
        if (!v) return null;
        const yf = this.yearFilter();
        const ps = rollupProjectsForGpTotals(
            yf ? v.projects.filter(p => p.sanctionedYear === yf) : v.projects
        );
        const stats = aggregateGpStatusStats(ps);
        const rivalryCount = ps.filter(p => this.getIssueType(p) === 'Local Rivalry').length;
        return { ...stats, rivalryCount };
    });

    /** Sum of Est. Cost for rows currently shown in the GP project table. */
    gpTableTotalCost = computed(() =>
        this.filteredProjectsFull().reduce((s, p) => s + (p.cost ?? 0), 0)
    );

    filteredProjectsFull = computed<Project[]>(() => {
        const v = this.selectedVillage();
        if (!v) return [];
        const q   = this.searchTerm().toLowerCase().trim();
        const sf  = this.statusFilter();
        const qf  = this.activeQuickFilter();
        const yf  = this.yearFilter();
        const sch = this.schemeFilter();
        const base = rollupProjectsForGpTotals(v.projects);
        return base
            .filter(p => {
                const matchY   = !yf  || p.sanctionedYear === yf;
                const matchSch = !sch || p.scheme === sch;
                const matchQ   = !q   || p.name.toLowerCase().includes(q) ||
                    (p.loc || '').toLowerCase().includes(q) ||
                    (p.scheme || '').toLowerCase().includes(q) ||
                    (p.executorName || '').toLowerCase().includes(q);
                const matchSf = !sf || p.status === sf;
                let matchQf = true;
                if (qf === 'delayed') matchQf = p.status === 'Stuck';
                else if (qf === 'stuck-ns') matchQf = p.status === 'Stuck' || p.status === 'Planned';
                else if (qf === 'rivalry') matchQf = this.getIssueType(p) === 'Local Rivalry';
                else if (qf === 'intervention') matchQf = !!p.recurringIntervention;
                return matchY && matchSch && matchQ && matchSf && matchQf;
            })
            .sort((a, b) =>
                (a.name || '').trim().localeCompare((b.name || '').trim(), undefined, { sensitivity: 'base' }) ||
                (a.village || a.loc || '').trim().localeCompare((b.village || b.loc || '').trim(), undefined, { sensitivity: 'base' })
            );
    });

    sortedProjectsForView = computed<Project[]>(() => {
        const projects = [...this.filteredProjectsFull()];
        const sort     = this.projectSortBy();
        if (sort === 'alphabetical') {
            return projects.sort((a, b) =>
                (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
            );
        }
        if (sort === 'cost') {
            return projects.sort((a, b) => (b.cost || 0) - (a.cost || 0));
        }
        const priority: Record<string, number> = {
            Stuck: 0, Planned: 1, 'In Progress': 2, Completed: 3,
        };
        return projects.sort((a, b) => {
            const pa = priority[a.status] ?? 2;
            const pb = priority[b.status] ?? 2;
            if (pa !== pb) return pa - pb;
            return (a.physical ?? 0) - (b.physical ?? 0);
        });
    });

    // ── Desktop GP table: column sort + "missing first" toggles ────────────
    // Scoped to sortedProjectsForTable() only, so the mobile card view and
    // the dropdown-driven sortedProjectsForView() keep their existing behavior.
    tableSortKey = signal<GpTableSortKey | null>(null);
    tableSortDir = signal<'asc' | 'desc'>('asc');
    missingExecutantFirst = signal(false);
    missingContactFirst   = signal(false);

    toggleTableSort(key: GpTableSortKey): void {
        if (this.tableSortKey() !== key) {
            this.tableSortKey.set(key);
            this.tableSortDir.set('asc');
        } else if (this.tableSortDir() === 'asc') {
            this.tableSortDir.set('desc');
        } else {
            // Third click clears the column sort, back to the dropdown order.
            this.tableSortKey.set(null);
            this.tableSortDir.set('asc');
        }
    }

    tableSortIcon(key: GpTableSortKey): string {
        if (this.tableSortKey() !== key) return 'fa-sort';
        return this.tableSortDir() === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
    }

    isExecutantMissing(p: Project): boolean {
        return !this.getExecutantDisplayName(p).trim();
    }

    isContactMissing(p: Project): boolean {
        return !(p.executorNumber ?? '').trim();
    }

    /** Whether the current table has any rows missing these fields — the
     *  "Not provided first" checkboxes are hidden when there is nothing to float. */
    hasMissingExecutants = computed(() =>
        this.filteredProjectsFull().some(p => this.isExecutantMissing(p))
    );
    hasMissingContacts = computed(() =>
        this.filteredProjectsFull().some(p => this.isContactMissing(p))
    );

    private compareByColumn(a: Project, b: Project, key: GpTableSortKey): number {
        const cmp = (x?: string | null, y?: string | null, opts?: Intl.CollatorOptions) =>
            (x || '').trim().localeCompare((y || '').trim(), undefined, { sensitivity: 'base', ...opts });
        switch (key) {
            case 'village':   return cmp(a.loc || a.village, b.loc || b.village);
            case 'scheme':    return cmp(a.scheme, b.scheme);
            case 'name':      return cmp(a.name, b.name);
            case 'cost':      return (a.cost ?? 0) - (b.cost ?? 0);
            case 'executant': return cmp(this.getExecutantDisplayName(a), this.getExecutantDisplayName(b));
            case 'contact':   return cmp(a.executorNumber, b.executorNumber, { numeric: true });
            case 'status': {
                const order: Record<string, number> = {
                    Stuck: 0, Planned: 1, 'In Progress': 2, Completed: 3,
                };
                return (order[a.status] ?? 2) - (order[b.status] ?? 2);
            }
        }
    }

    /** Rows for the desktop GP projects table: layers header-column sorting and
     *  the "missing first" checkboxes on top of the dropdown sort. */
    sortedProjectsForTable = computed<Project[]>(() => {
        const rows = [...this.sortedProjectsForView()];
        const key  = this.tableSortKey();
        if (key) {
            const dir = this.tableSortDir() === 'asc' ? 1 : -1;
            rows.sort((a, b) => this.compareByColumn(a, b, key) * dir);
        }
        // Either checkbox: rows missing a name OR a number float to the top,
        // rows missing both come first. Same logic for both checkboxes.
        // Read both signals unconditionally so both are tracked as dependencies.
        // A checkbox hidden in the header (nothing missing) must not keep sorting.
        const execFirst    = this.missingExecutantFirst() && this.hasMissingExecutants();
        const contactFirst = this.missingContactFirst() && this.hasMissingContacts();
        if (execFirst || contactFirst) {
            const rank = (p: Project) =>
                (this.isExecutantMissing(p) ? -1 : 0) +
                (this.isContactMissing(p) ? -1 : 0);
            // Stable sort: rows keep their previous order within each group.
            rows.sort((a, b) => rank(a) - rank(b));
        }
        return rows;
    });

    // ── Inline edit ────────────────────────────────────────────────────────
    editingProjectId = signal<string | null>(null);
    editForm!: FormGroup;
    isSaving  = signal(false);
    saveError = signal<string | null>(null);

    // ── Dynamic list modal ─────────────────────────────────────────────────
    listFilter = signal<VlListFilter | null>(null);

    listTitle = computed(() => {
        const lbl  = this.gpLabel();
        const full = this.gpLabelFull();
        const plur = this.gpLabelPlural();
        const blk  = this.displayBlockName();
        const vil  = this.villageLabel(this.selectedVillage()?.village ?? '');
        switch (this.listFilter()) {
            case 'totalGPs':           return `Block: ${blk} — ${full}s`;
            case 'totalProjectsBlock': return `${blk} — All Projects`;
            case 'sanctionedBlock':    return `${blk} — Sanctioned Budget`;
            case 'onTrackBlock':       return `${blk} — On-Track Projects`;
            case 'inProgressBlock':    return `${blk} — In-Progress Projects`;
            case 'delayedBlock':       return `${blk} — Delayed Projects (> 60 Days)`;
            case 'avgProgressBlock':   return `${blk} — Projects by Progress`;
            case 'stuckBlock':         return `${blk} — Stuck / Not Started Projects`;
            case 'totalProjectsGP':    return `${vil} ${lbl} — All Projects`;
            case 'totalCostGP':        return `${vil} ${lbl} — Project Cost`;
            case 'onTrackGP':          return `${vil} ${lbl} — On Track`;
            case 'inProgressGP':       return `${vil} ${lbl} — In Progress`;
            case 'delayedGP':          return `${vil} ${lbl} — Delayed`;
            case 'stuckGP':            return `${vil} ${lbl} — Stuck / Not Started`;
            case 'rivalryGP':          return `${vil} ${lbl} — Rivalry Issues`;
            default:                   return '';
        }
    });

    listShowsVillages = computed(() => this.listFilter() === 'totalGPs');

    listVillages = computed<VillageGroup[]>(() => {
        const block = this.selectedBlock();
        if (!block) return [];
        let result: VillageGroup[];
        switch (this.listFilter()) {
            case 'totalGPs': result = block.villages; break;
            default:         return [];
        }
        return [...result].sort((a, b) =>
            (a.village || '').trim().localeCompare((b.village || '').trim(), undefined, { sensitivity: 'base' })
        );
    });

    listProjects = computed<Project[]>(() => {
        const block   = this.selectedBlock();
        const village = this.selectedVillage();
        const blockProjects = block ? block.villages.flatMap(v => v.projects) : [];
        const gpProjects    = village ? village.projects : [];

        let result: Project[];
        switch (this.listFilter()) {
            case 'totalProjectsBlock':
            case 'sanctionedBlock':
            case 'avgProgressBlock':  result = blockProjects; break;
            case 'onTrackBlock':      result = blockProjects.filter(p => isFeOnTrack(p)); break;
            case 'inProgressBlock':   result = blockProjects.filter(p => isFeInProgress(p)); break;
            case 'delayedBlock':      result = blockProjects.filter(p => isFeDelayed(p)); break;
            case 'stuckBlock':        result = blockProjects.filter(p => p.status === 'Stuck' || p.status === 'Planned'); break;

            case 'totalProjectsGP':
            case 'totalCostGP':       result = rollupProjectsForGpTotals(gpProjects); break;
            case 'onTrackGP':         result = gpProjects.filter(p => p.status === 'Completed'); break;
            case 'inProgressGP':      result = gpProjects.filter(p => p.status === 'In Progress'); break;
            case 'delayedGP':         result = gpProjects.filter(p => p.status === 'Stuck'); break;
            case 'stuckGP':           result = gpProjects.filter(p => p.status === 'Stuck' || p.status === 'Planned'); break;
            case 'rivalryGP':         result = gpProjects.filter(p => this.getIssueType(p) === 'Local Rivalry'); break;

            default:                  return [];
        }
        return [...result].sort((a, b) =>
            (a.name || '').trim().localeCompare((b.name || '').trim(), undefined, { sensitivity: 'base' }) ||
            (a.village || a.loc || '').trim().localeCompare((b.village || b.loc || '').trim(), undefined, { sensitivity: 'base' })
        );
    });

    openList(filter: VlListFilter): void { this.listFilter.set(filter); }
    closeList(): void { this.listFilter.set(null); }

    ngOnInit(): void {
        const name = this.route.snapshot.paramMap.get('block') ?? '';
        this.blockName.set(decodeURIComponent(name));

        this.svc.getBlockGroups$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: groups => {
                    const found = groups.find(b => b.block === this.blockName());
                    this.selectedBlock.set(found ?? null);
                    // Keep selected village in sync on real-time update
                    const vil = this.selectedVillage();
                    if (vil && found) {
                        const updated = found.villages.find(v => v.village === vil.village);
                        if (updated) this.selectedVillage.set(updated);
                    } else {
                        this.restoreGpFromNavigationState(found);
                    }
                    this.isLoading.set(false);
                },
                error: () => this.isLoading.set(false)
            });

        this.timerHandle = setInterval(() => {
            this.currentTime.set(new Date());
        }, 60000);
    }

    ngOnDestroy(): void {
        if (this.timerHandle) clearInterval(this.timerHandle);
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Navigation ─────────────────────────────────────────────────────────
    goHome(): void { this.router.navigate(['/home']); }

    goBackToBlocks(): void { this.router.navigate(['/field-execution']); }

    selectVillage(village: VillageGroup): void {
        this.selectedVillage.set(village);
        this.searchTerm.set('');
        this.statusFilter.set('');
        this.yearFilter.set('');
        this.schemeFilter.set('');
        this.tableSortKey.set(null);
        this.tableSortDir.set('asc');
        this.missingExecutantFirst.set(false);
        this.missingContactFirst.set(false);
        this.screen.set('projects');
    }

    backToVillages(): void {
        this.selectedVillage.set(null);
        this.cancelEdit();
        this.searchTerm.set('');
        this.yearFilter.set('');
        this.schemeFilter.set('');
        this.screen.set('villages');
    }

    navigateToExecutant(p: Project): void {
        const num  = (p.executorNumber ?? '').trim();
        const name = (p.executorName ?? p.executingAgency ?? '').trim();
        if (!num && !name) return;
        const key = num
            ? `number:${num}`
            : `name:${encodeURIComponent(name)}`;
        const navState: FeExecutantNavState = {
            returnUrl: this.router.url,
            returnGp: this.selectedVillage()?.village ?? (p.gp || p.village || p.loc || ''),
            returnScreen: this.screen(),
        };
        this.router.navigate(['/field-execution/executant', key], { state: navState });
    }

    private restoreGpFromNavigationState(block: BlockGroup | null | undefined): void {
        if (!block) return;
        const nav = history.state as FeVillageListNavState | undefined;
        if (nav?.screen !== 'projects' || !nav.openGp?.trim()) return;

        const gpName = nav.openGp.trim();
        const village = block.villages.find(v =>
            v.village.trim().localeCompare(gpName, undefined, { sensitivity: 'base' }) === 0
        );
        if (village) this.selectVillage(village);
    }

    // ── Inline edit ────────────────────────────────────────────────────────
    openEdit(project: Project): void {
        this.editingProjectId.set(project.id);
        this.editForm = this.fb.group({
            status:         [project.status,        Validators.required],
            remarks:        [project.remarks  ?? ''],
            executorName:   [project.executorName   ?? project.executingAgency ?? ''],
            executorNumber: [project.executorNumber ?? ''],
            physical:       [project.physical ?? 0, [Validators.min(0), Validators.max(100)]],
            cost:           [project.cost ?? 0,     [Validators.required, Validators.min(0)]],
        });
        this.saveError.set(null);
    }

    cancelEdit(): void {
        this.editingProjectId.set(null);
        this.saveError.set(null);
    }

    async saveEdit(projectId: string): Promise<void> {
        if (this.editForm.invalid) { this.editForm.markAllAsTouched(); return; }
        this.isSaving.set(true);
        this.saveError.set(null);
        try {
            const v = this.editForm.value;
            await this.svc.updateProject(projectId, {
                status:         v.status,
                remarks:        v.remarks,
                executorName:   v.executorName,
                executorNumber: v.executorNumber,
                physical:       Number(v.physical),
                cost:           Number(v.cost) || 0,
            });
            this.editingProjectId.set(null);
        } catch {
            this.saveError.set('Failed to save. Please try again.');
        } finally {
            this.isSaving.set(false);
        }
    }

    // ── Export ──────────────────────────────────────────────────────────────
    exportVillage(): void {
        const village = this.selectedVillage();
        if (!village) return;
        this.svc.exportToExcel(village.projects, `field-execution-${village.village}`);
    }

    exportBlock(): void {
        const block = this.selectedBlock();
        if (!block) return;
        const all = block.villages.flatMap(v => v.projects);
        this.svc.exportToExcel(all, `field-execution-${block.block}`);
    }

    // ── GP project view helpers ─────────────────────────────────────────────
    setQuickFilter(filter: string): void {
        this.activeQuickFilter.set(this.activeQuickFilter() === filter ? '' : filter);
    }

    setProjectSort(value: string): void {
        this.projectSortBy.set(value as ProjectSortOption);
        // A column sort would override the dropdown; clear it so the change shows.
        this.tableSortKey.set(null);
        this.tableSortDir.set('asc');
    }

    goBack(): void {
        this.screen() === 'projects' ? this.backToVillages() : this.goBackToBlocks();
    }

    doExport(): void {
        this.screen() === 'projects' ? this.exportVillage() : this.exportBlock();
    }

    getIssueType(p: Project): string {
        const r = (p.remarks || '').toLowerCase();
        if (r.includes('rival') || r.includes('dispute')) return 'Local Rivalry';
        if (r.includes('fund') || r.includes('uc pending')) return 'Fund Pending';
        if (r.includes('contractor')) return 'Contractor Delay';
        if (r.includes('administrative') || r.includes('tech sanction') || r.includes('sanction awaited')) return 'Administrative Delay';
        if (r.includes('land')) return 'Land Issue';
        return '–';
    }

    getExecutantRisk(p: Project): { label: string; level: string } {
        if (p.status === 'Completed' || (p.physical ?? 0) >= 70) return { label: 'Low (Reliable)', level: 'low' };
        if (p.status === 'In Progress') return { label: 'Medium (Moderate)', level: 'medium' };
        return { label: 'High (At Risk)', level: 'high' };
    }

    getExecutantType(p: Project): string {
        const name = this.getExecutantDisplayName(p);
        if (!name) return '';
        if (name.toLowerCase().includes('committee')) return 'Committee';
        return splitExecutantNames(name).length > 1 ? 'Committee' : 'Individual';
    }

    getExecutantDisplayName(p: Project): string {
        return p.executorName || p.executingAgency || p.executant?.name || '';
    }

    /** Individual executant names (lowercased, trimmed) that appear on 2+ projects in the
     *  current list — counted per person, so a name shared across different joint/committee
     *  entries (e.g. "Bhaskar Rout, Kesab Nayak" and "Kesab Nayak, Muktikanta...") still counts. */
    duplicateExecutantNames = computed<Set<string>>(() => {
        const counts = new Map<string, number>();
        for (const p of this.filteredProjectsFull()) {
            const parts = new Set(
                splitExecutantNames(this.getExecutantDisplayName(p)).map(n => n.toLowerCase())
            );
            for (const name of parts) {
                counts.set(name, (counts.get(name) ?? 0) + 1);
            }
        }
        const duplicates = new Set<string>();
        for (const [name, count] of counts) {
            if (count > 1) duplicates.add(name);
        }
        return duplicates;
    });

    /** True if any individual named on this project's executant entry also appears on another project. */
    isDuplicateExecutant(p: Project): boolean {
        const parts = splitExecutantNames(this.getExecutantDisplayName(p)).map(n => n.toLowerCase());
        return parts.some(name => this.duplicateExecutantNames().has(name));
    }

    formatCostLakh = formatCostLakh;

    /** Compact sanctioned lakhs for mobile KPI (e.g. 2273 L → 23K). */
    sanctionedCompact(amount: number | undefined | null): string {
        const v = Math.round(amount ?? 0);
        if (v >= 100) return Math.round(v / 100) + 'K';
        return v.toLocaleString('en-IN');
    }

    stuckNotStartedStats(projects: Project[]): { count: number; percent: number } {
        const total = projects.length;
        const count = projects.filter(p => p.status === 'Stuck' || p.status === 'Planned').length;
        return { count, percent: total > 0 ? Math.round((count / total) * 100) : 0 };
    }

    villageStuckNotStarted(village: VillageGroup): { count: number; percent: number } {
        return this.stuckNotStartedStats(village.projects);
    }

    /** Count of projects in a GP missing an executant name or a contact number. */
    villageExecutantStatus(village: VillageGroup): { allAvailable: boolean; missingCount: number } {
        const missingCount = village.projects.filter(p =>
            !this.getExecutantDisplayName(p) || !(p.executorNumber ?? '').trim()
        ).length;
        return { allAvailable: missingCount === 0, missingCount };
    }

    statusLabel(status: string): string {
        const map: Record<string, string> = {
            'Completed': 'On Track', 'In Progress': 'Work Started',
            'Stuck': 'Delayed', 'Planned': 'Not Started',
        };
        return map[status] || status;
    }

    statusChipClass(status: string): string {
        const map: Record<string, string> = {
            'Completed': 'sc-completed', 'In Progress': 'sc-in-progress',
            'Stuck': 'sc-stuck', 'Planned': 'sc-planned',
        };
        return map[status] || 'sc-planned';
    }

    rowBorderClass(status: string): string {
        const map: Record<string, string> = {
            'Completed': 'row-border-green', 'In Progress': 'row-border-amber',
            'Stuck': 'row-border-red', 'Planned': 'row-border-gray',
        };
        return map[status] || 'row-border-gray';
    }

    riskChipClass(p: Project): string {
        return `risk-${this.getExecutantRisk(p).level}`;
    }

    getDaysSinceUpdate(p: Project): string {
        if (!p.photoUploadedAt) return '–';
        const days = Math.floor((Date.now() - new Date(p.photoUploadedAt).getTime()) / 86400000);
        return days === 0 ? 'Today' : `${days} day${days !== 1 ? 's' : ''}`;
    }

    isUpdateStale(p: Project): boolean {
        if (!p.photoUploadedAt) return false;
        return Math.floor((Date.now() - new Date(p.photoUploadedAt).getTime()) / 86400000) > 5;
    }

    // ── UI helpers ──────────────────────────────────────────────────────────
    statusBadge(status: string): string {
        switch (status) {
            case 'Completed':   return 'bg-emerald-100 text-emerald-800 border-emerald-300';
            case 'In Progress': return 'bg-blue-100 text-blue-800 border-blue-300';
            case 'Stuck':       return 'bg-red-100 text-red-800 border-red-300';
            default:            return 'bg-slate-100 text-slate-600 border-slate-300';
        }
    }

    statusDot(status: string): string {
        switch (status) {
            case 'Completed':   return 'bg-emerald-500';
            case 'In Progress': return 'bg-blue-500';
            case 'Stuck':       return 'bg-red-500';
            default:            return 'bg-slate-400';
        }
    }

    progressBarColor(status: string): string {
        switch (status) {
            case 'Completed':   return 'bg-emerald-500';
            case 'In Progress': return 'bg-blue-500';
            case 'Stuck':       return 'bg-red-500';
            default:            return 'bg-slate-300';
        }
    }

    totalCost(projects: Project[]): number {
        return rollupProjectsForGpTotals(projects).reduce((s, p) => s + (p.cost ?? 0), 0);
    }

    totalSpent(projects: Project[]): number {
        return projects.reduce((s, p) => s + (p.spent ?? 0), 0);
    }

    completedCount(projects: Project[]): number {
        return projects.filter(p => p.status === 'Completed').length;
    }

    stuckCount(projects: Project[]): number {
        return projects.filter(p => p.status === 'Stuck').length;
    }

    getVillagesByBlock(): VillageGroup[] {
        return this.selectedBlock()?.villages ?? [];
    }
}
